import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import supabase from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from '../components/ImageWithFallback';
import YouTubeChannelEmbed from '../components/YouTubeChannelEmbed';
import ShareButton from '../components/ShareButton';
import * as FiIcons from 'react-icons/fi';

const {
  FiImage,
  FiVideo,
  FiFileText,
  FiCalendar,
  FiTag,
  FiX,
  FiPlay,
  FiArrowRight,
  FiYoutube,
} = FiIcons;

const normalizeArray = (value) => (Array.isArray(value) ? value : []);
const safeDate = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('el-GR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const MediaWallPage = () => {
  const [gallery, setGallery] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [tag, setTag] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const [galleryResult, newsResult] = await Promise.all([
        supabase
          .from('gallery_items_aox1234')
          .select('*')
          .order('date', { ascending: false })
          .order('sort_order', { ascending: true })
          .limit(80),
        supabase
          .from('news_aox1234')
          .select('id,title,excerpt,content,image,image_url,category,published_at,facebook_url,status')
          .eq('status', 'published')
          .lte('published_at', new Date().toISOString())
          .order('published_at', { ascending: false })
          .limit(30),
      ]);

      if (cancelled) return;
      setGallery(Array.isArray(galleryResult.data) ? galleryResult.data : []);
      setNews(Array.isArray(newsResult.data) ? newsResult.data : []);
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const tags = useMemo(() => {
    const values = new Set();
    gallery.forEach((item) => normalizeArray(item.tags).forEach((itemTag) => {
      const clean = String(itemTag || '').trim();
      if (clean && clean.toLowerCase() !== 'academy carousel') values.add(clean);
    }));
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'el'));
  }, [gallery]);

  const items = useMemo(() => {
    const galleryItems = gallery.map((item) => ({
      ...item,
      mediaKind: item.type === 'video' ? 'video' : 'image',
      mediaDate: item.date,
      sortDate: safeDate(item.date),
    }));

    const newsItems = news.map((item) => ({
      ...item,
      mediaKind: 'news',
      url: item.image || item.image_url || '',
      mediaDate: item.published_at,
      sortDate: safeDate(item.published_at),
      tags: item.category ? [item.category] : [],
    }));

    return [...galleryItems, ...newsItems]
      .filter((item) => filter === 'all' || item.mediaKind === filter)
      .filter((item) => tag === 'all' || normalizeArray(item.tags).includes(tag))
      .sort((a, b) => b.sortDate - a.sortDate);
  }, [gallery, news, filter, tag]);

  const counts = useMemo(() => ({
    all: gallery.length + news.length,
    image: gallery.filter((item) => item.type !== 'video').length,
    video: gallery.filter((item) => item.type === 'video').length,
    news: news.length,
  }), [gallery, news]);

  const filters = [
    { value: 'all', label: 'Όλα', icon: FiTag },
    { value: 'image', label: 'Φωτογραφίες', icon: FiImage },
    { value: 'video', label: 'Βίντεο', icon: FiVideo },
    { value: 'news', label: 'Νέα', icon: FiFileText },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#03152f] via-primary-900 to-primary-600 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,.35), transparent 22%), radial-gradient(circle at 80% 65%, rgba(55,160,255,.45), transparent 28%)' }} />
        <div className="relative mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 lg:px-8 md:py-20">
          <div className="text-xs font-black uppercase tracking-[0.28em] text-blue-200">AOX Media</div>
          <h1 className="mt-3 text-4xl font-black md:text-6xl">Media Wall</h1>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-blue-100">Φωτογραφίες, βίντεο, νέα και στιγμές από την Α' Ομάδα, την Κ17 και την Ακαδημία του ΑΟ Χανιά.</p>
          <div className="mt-7 flex justify-center">
            <ShareButton title="AOX Media Wall - Α.Ο. Χανιά WFC" description="Φωτογραφίες, βίντεο και νέα του ΑΟ Χανιά WFC" variant="outline" className="border-white/25 bg-white/10 text-white" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-10">
          <YouTubeChannelEmbed title="AOX TV" description="Τα τελευταία uploads από το επίσημο YouTube κανάλι του ΑΟ Χανιά WFC." />
        </div>

        <div className="mb-7 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {filters.map((item) => (
              <button
                key={item.value}
                onClick={() => setFilter(item.value)}
                className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === item.value ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                <SafeIcon icon={item.icon} className="mr-2 h-4 w-4" />
                {item.label}
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${filter === item.value ? 'bg-white/20' : 'bg-white'}`}>{counts[item.value]}</span>
              </button>
            ))}
          </div>

          {tags.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <span className="mr-1 text-xs font-black uppercase tracking-wider text-slate-400">Ετικέτες</span>
              <button onClick={() => setTag('all')} className={`rounded-full px-3 py-1.5 text-xs font-bold ${tag === 'all' ? 'bg-primary-100 text-primary-800' : 'bg-slate-50 text-slate-600'}`}>Όλες</button>
              {tags.map((itemTag) => <button key={itemTag} onClick={() => setTag(itemTag)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${tag === itemTag ? 'bg-primary-100 text-primary-800' : 'bg-slate-50 text-slate-600'}`}>{itemTag}</button>)}
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-500">Φόρτωση Media Wall...</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500">Δεν υπάρχει περιεχόμενο για το συγκεκριμένο φίλτρο.</div>
        ) : (
          <div className="columns-1 gap-5 sm:columns-2 lg:columns-3 xl:columns-4">
            {items.map((item, index) => (
              <motion.article
                key={`${item.mediaKind}-${item.id}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.025, 0.3) }}
                className="mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
              >
                {item.mediaKind === 'news' ? (
                  <Link to={`/?news=${item.id}`} className="block group">
                    {item.url && <div className="relative overflow-hidden bg-slate-100"><ImageWithFallback src={item.url} alt={item.title} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" /><div className="absolute left-3 top-3 rounded-full bg-primary-600 px-3 py-1 text-xs font-black text-white">ΝΕΑ</div></div>}
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-slate-400"><SafeIcon icon={FiCalendar} className="h-3.5 w-3.5" />{formatDate(item.mediaDate)}</div>
                      <h2 className="mt-2 text-lg font-black text-slate-900">{item.title}</h2>
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{item.excerpt || item.content || ''}</p>
                      <div className="mt-4 inline-flex items-center text-sm font-black text-primary-700">Διαβάστε περισσότερα <SafeIcon icon={FiArrowRight} className="ml-1 h-4 w-4" /></div>
                    </div>
                  </Link>
                ) : (
                  <button onClick={() => setSelected(item)} className="block w-full text-left group">
                    <div className="relative bg-slate-900">
                      {item.mediaKind === 'video' ? (
                        <video src={item.url} className="w-full max-h-[460px] object-cover" preload="metadata" muted />
                      ) : (
                        <ImageWithFallback src={item.url} alt={item.title} className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                      {item.mediaKind === 'video' && <div className="absolute inset-0 flex items-center justify-center"><div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-primary-700 shadow-xl"><SafeIcon icon={FiPlay} className="ml-1 h-6 w-6" /></div></div>}
                      <div className="absolute bottom-3 left-3 right-3 text-white"><div className="text-xs text-white/75">{formatDate(item.mediaDate)}</div><div className="mt-1 font-black drop-shadow">{item.title}</div></div>
                    </div>
                  </button>
                )}
              </motion.article>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {selected && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelected(null)} className="absolute inset-0 bg-black/85 backdrop-blur-sm" aria-label="Κλείσιμο" />
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} className="relative z-10 max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-black shadow-2xl">
              <button onClick={() => setSelected(null)} className="absolute right-4 top-4 z-20 rounded-full bg-black/60 p-2 text-white"><SafeIcon icon={FiX} className="h-6 w-6" /></button>
              {selected.mediaKind === 'video' ? <video src={selected.url} controls autoPlay className="max-h-[78vh] w-full bg-black object-contain" /> : <ImageWithFallback src={selected.url} alt={selected.title} className="max-h-[78vh] w-full bg-black object-contain" />}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-950 p-4 text-white"><div><div className="font-black">{selected.title}</div><div className="mt-1 text-xs text-white/60">{formatDate(selected.mediaDate)}</div></div>{selected.mediaKind === 'video' && <div className="inline-flex items-center text-xs font-bold text-red-300"><SafeIcon icon={FiYoutube} className="mr-1 h-4 w-4" />VIDEO</div>}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaWallPage;
